import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ContractsService } from '../../../services/contracts.service';
import {
  B2bPortalPermission,
  B2bPortalRole,
  B2bPortalUser,
  B2bPortalUsersService
} from '../../../services/b2b-portal-users.service';
import { ErrorService } from '../../../shared/services/error.service';
import { ConfirmationService } from '../../../shared/services/confirmation.service';
import { TableAction } from '../../../shared/components/table/table.component';

type CenterTab = 'users' | 'access';
type PermissionModuleGroup = { module: string; items: B2bPortalPermission[] };

@Component({
  selector: 'app-b2b-portal-access-center',
  templateUrl: './b2b-portal-access-center.component.html',
  styleUrls: ['./b2b-portal-access-center.component.css']
})
export class B2bPortalAccessCenterComponent implements OnInit {
  tab: CenterTab = 'users';
  contractId: number | null = null;
  contractNumber = '';
  contractName = '';

  users: B2bPortalUser[] = [];
  isLoadingUsers = false;
  pageNumber = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;
  searchTerm = '';

  roles: B2bPortalRole[] = [];
  permissions: B2bPortalPermission[] = [];
  permissionGroups: PermissionModuleGroup[] = [];
  selectedRoleCode: string | null = null;
  /** Códigos marcados do perfil selecionado (nova referência a cada alteração p/ CD). */
  selectedPermissionCodes = new Set<string>();
  isLoadingAccess = false;
  isSavingAccess = false;
  accessDirty = false;

  showCreateRole = false;
  isCreatingRole = false;
  newRoleCode = '';
  newRoleName = '';
  newRoleDescription = '';
  createRoleError = '';
  accessError = '';

  columns = [
    { titulo: 'Nome', label: 'name' },
    { titulo: 'E-mail', label: 'email' },
    { titulo: 'Perfil', label: 'roleName' },
    {
      titulo: 'Status',
      label: 'activeLabel',
    },
    {
      titulo: 'Módulos',
      label: 'modulesLabel',
    },
  ];

  actions: TableAction[] = [
    { label: 'Editar', icon: 'fas fa-edit', action: 'edit', class: 'btn-edit' },
    { label: 'Ativar/Inativar', icon: 'fas fa-toggle-on', action: 'toggle', class: 'btn-view' },
    { label: 'Excluir', icon: 'fas fa-trash', action: 'delete', class: 'btn-delete' },
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private contractsService: ContractsService,
    private portalUsers: B2bPortalUsersService,
    private errorService: ErrorService,
    private confirmationService: ConfirmationService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.contractId = +params['id'];
      if (!this.contractId) return;
      this.loadContractInfo();
      this.loadUsers();
      this.loadAccessCatalog();
    });
  }

  setTab(tab: CenterTab): void {
    this.tab = tab;
    if (tab === 'access' && !this.roles.length) {
      this.loadAccessCatalog();
    }
  }

  loadContractInfo(): void {
    if (!this.contractId) return;
    this.contractsService.getB2bContractById(this.contractId).subscribe({
      next: (contract) => {
        this.contractNumber = contract.contractNumber || '';
        this.contractName = contract.contractName || '';
      },
      error: (err) => this.errorService.handleHttpError(err)
    });
  }

  loadUsers(): void {
    if (!this.contractId) return;
    this.isLoadingUsers = true;
    this.portalUsers.listByContract(this.contractId, this.pageNumber, this.pageSize, this.searchTerm).subscribe({
      next: (res) => {
        this.users = res.data.map(u => ({
          ...u,
          activeLabel: u.active ? 'Ativo' : 'Inativo',
          modulesLabel: (u.modules || []).join(', ') || '—',
        } as any));
        this.totalItems = res.totalItems;
        this.totalPages = res.totalPages;
        this.pageNumber = res.currentPage;
        this.isLoadingUsers = false;
      },
      error: (err) => {
        this.isLoadingUsers = false;
        this.errorService.handleHttpError(err);
      }
    });
  }

  loadAccessCatalog(): void {
    this.isLoadingAccess = true;
    this.portalUsers.seed().subscribe({
      next: () => this.fetchRolesAndPermissions(),
      error: () => this.fetchRolesAndPermissions()
    });
  }

  private fetchRolesAndPermissions(): void {
    this.accessError = '';
    this.portalUsers.listRoles().subscribe({
      next: (roles) => {
        this.roles = roles;
        if (!this.selectedRoleCode && roles.length) {
          this.selectRole(roles[0].code);
        } else if (this.selectedRoleCode) {
          this.selectRole(this.selectedRoleCode);
        }
        this.isLoadingAccess = false;
      },
      error: (err) => {
        this.isLoadingAccess = false;
        this.handlePortalAuthError(err, 'Não foi possível listar os perfis do portal.');
      }
    });

    this.portalUsers.listPermissions().subscribe({
      next: (permissions) => {
        this.permissions = permissions;
        this.rebuildPermissionGroups();
        this.cdr.markForCheck();
      },
      error: (err) => this.handlePortalAuthError(err, 'Não foi possível listar as permissões do portal.')
    });
  }

  private rebuildPermissionGroups(): void {
    const map = new Map<string, B2bPortalPermission[]>();
    for (const p of this.permissions) {
      const key = p.module || 'Outros';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(p);
    }
    this.permissionGroups = Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([module, items]) => ({
        module,
        items: items.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')),
      }));
  }

  private handlePortalAuthError(err: any, fallback: string): void {
    if (err?.status === 401) {
      this.accessError =
        'Sem autorização no Auth B2B (StaffJwt). Confirme JwtAuth no ERP e StaffJwt__Issuer/Secret no Auth B2B.';
      this.errorService.showError(this.accessError, 'error');
      return;
    }
    const msg = err?.error?.message || err?.error?.Message || fallback;
    this.accessError = msg;
    this.errorService.handleHttpError(err, fallback);
  }

  selectRole(code: string): void {
    this.selectedRoleCode = code;
    const role = this.roles.find(r => r.code === code);
    this.selectedPermissionCodes = new Set(role?.permissionCodes ?? []);
    this.accessDirty = false;
    this.cdr.markForCheck();
  }

  get selectedRole(): B2bPortalRole | undefined {
    return this.roles.find(r => r.code === this.selectedRoleCode);
  }

  openCreateRole(): void {
    this.showCreateRole = true;
    this.createRoleError = '';
    this.newRoleCode = '';
    this.newRoleName = '';
    this.newRoleDescription = '';
  }

  cancelCreateRole(): void {
    this.showCreateRole = false;
    this.createRoleError = '';
  }

  createRole(): void {
    const code = (this.newRoleCode || '').trim().toUpperCase();
    const name = (this.newRoleName || '').trim();
    if (!code || !name) {
      this.createRoleError = 'Informe código e nome do perfil.';
      return;
    }
    this.isCreatingRole = true;
    this.createRoleError = '';
    this.portalUsers.createRole({
      code,
      name,
      description: (this.newRoleDescription || '').trim() || undefined,
      permissionCodes: [],
    }).subscribe({
      next: (role) => {
        this.roles = [...this.roles, role];
        this.selectRole(role.code);
        this.showCreateRole = false;
        this.isCreatingRole = false;
      },
      error: (err) => {
        this.isCreatingRole = false;
        this.createRoleError = err?.error?.message || err?.error?.Message || 'Falha ao criar perfil.';
        this.handlePortalAuthError(err, this.createRoleError);
      }
    });
  }

  deleteSelectedRole(): void {
    const role = this.selectedRole;
    if (!role || role.isSystem) return;
    this.confirmationService.confirmDelete(`${role.name} (${role.code})`, 'o perfil do portal').subscribe({
      next: (ok) => {
        if (!ok) return;
        this.portalUsers.deleteRole(role.code).subscribe({
          next: () => {
            this.roles = this.roles.filter(r => r.code !== role.code);
            this.selectedRoleCode = this.roles[0]?.code ?? null;
            if (this.selectedRoleCode) this.selectRole(this.selectedRoleCode);
            else this.selectedPermissionCodes = new Set();
          },
          error: (err) => this.handlePortalAuthError(err, 'Falha ao excluir perfil.')
        });
      }
    });
  }

  isPermissionChecked(code: string): boolean {
    return this.selectedPermissionCodes.has(code);
  }

  /** Nova referência de Set — mutação in-place não atualizava o [checked] na tela. */
  togglePermission(code: string, checked: boolean): void {
    const next = new Set(this.selectedPermissionCodes);
    if (checked) next.add(code);
    else next.delete(code);
    this.selectedPermissionCodes = next;
    this.accessDirty = true;
    this.cdr.detectChanges();
  }

  trackByPermissionCode(_index: number, perm: B2bPortalPermission): string {
    return perm.code;
  }

  trackByModule(_index: number, group: { module: string }): string {
    return group.module;
  }

  saveRoleAccess(): void {
    if (!this.selectedRoleCode) return;
    this.isSavingAccess = true;
    this.portalUsers.updateRolePermissions(this.selectedRoleCode, Array.from(this.selectedPermissionCodes)).subscribe({
      next: (role) => {
        this.roles = this.roles.map(r => r.code === role.code ? role : r);
        this.selectedPermissionCodes = new Set(role.permissionCodes ?? []);
        this.accessDirty = false;
        this.isSavingAccess = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isSavingAccess = false;
        this.errorService.handleHttpError(err);
      }
    });
  }

  onSearchChange(term: string): void {
    this.searchTerm = (term || '').trim();
    this.pageNumber = 1;
    this.loadUsers();
  }

  onPageChange(page: number): void {
    if (!page || page === this.pageNumber) return;
    this.pageNumber = page;
    this.loadUsers();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size > 0 ? size : 10;
    this.pageNumber = 1;
    this.loadUsers();
  }

  newUser(): void {
    if (!this.contractId) return;
    this.router.navigate(['/b2b/contracts', this.contractId, 'portal-users', 'new']);
  }

  onActionClick(event: { action: string; item: any }): void {
    const user = event.item as B2bPortalUser;
    if (event.action === 'edit') {
      this.router.navigate(['/b2b/contracts', this.contractId, 'portal-users', user.userId, 'edit']);
      return;
    }
    if (event.action === 'toggle') {
      this.portalUsers.setActive(user.userId, !user.active).subscribe({
        next: () => this.loadUsers(),
        error: (err) => this.errorService.handleHttpError(err)
      });
      return;
    }
    if (event.action === 'delete') {
      this.confirmationService.confirmDelete(`${user.name} (${user.email})`, 'o usuário do portal').subscribe({
        next: (ok) => {
          if (!ok) return;
          this.portalUsers.delete(user.userId).subscribe({
            next: () => this.loadUsers(),
            error: (err) => this.errorService.handleHttpError(err)
          });
        }
      });
    }
  }

  navigateBack(): void {
    if (!this.contractId) {
      this.router.navigate(['/b2b/contracts']);
      return;
    }
    this.router.navigate(['/b2b/contracts', this.contractId, 'view']);
  }
}
